import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Users, DollarSign, Gamepad2, TrendingUp, TrendingDown, Clock, Building2, ArrowUpCircle, ArrowDownCircle, Zap, Wallet, BarChart3, PieChart, Activity, CreditCard, Gift, RefreshCw, Download } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LineChart, Line } from 'recharts';

const PERIODS = [
  { id: 'day', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'week', label: 'This Week' },
  { id: 'month', label: 'This Month' },
  { id: 'all_time', label: 'All Time' },
];

const tooltipStyle = {
  background: '#0B0E14',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 10,
  color: '#fff',
  fontSize: 12,
};

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [gameStats, setGameStats] = useState(null);
  const [walletSummary, setWalletSummary] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [insights, setInsights] = useState(null);
  const [period, setPeriod] = useState('day');
  const [customRange, setCustomRange] = useState({ start: '', end: '' });
  const [rangeActive, setRangeActive] = useState(false);
  const [rangeVersion, setRangeVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => { fetchAll(); }, [period, rangeActive, rangeVersion]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      let insightsUrl = `/api/admin/analytics/insights?period=${period}`;
      if (rangeActive && customRange.start && customRange.end) {
        insightsUrl = `/api/admin/analytics/insights?start=${encodeURIComponent(customRange.start)}&end=${encodeURIComponent(customRange.end)}`;
      }
      const [dashRes, insightRes, revRes, usrRes, gmeRes, walRes, syncRes] = await Promise.all([
        api.get('/api/admin/dashboard').catch(() => ({ data: {} })),
        api.get(insightsUrl).catch(() => ({ data: {} })),
        api.get('/api/admin/analytics/revenue?days=30').catch(() => ({ data: {} })),
        api.get('/api/admin/analytics/users?days=30').catch(() => ({ data: {} })),
        api.get('/api/admin/analytics/games?days=30').catch(() => ({ data: {} })),
        api.get('/api/admin/analytics/wallets/summary').catch(() => ({ data: {} })),
        api.get('/api/admin/sync/status').catch(() => ({ data: {} })),
      ]);
      const s = dashRes.data.stats || {};
      const k = insightRes.data.kpis || {};
      setStats({
        total_players: k.lifetime_player_count ?? s.users?.total ?? 0,
        new_24h: k.period_signups ?? s.users?.new_24h ?? 0,
        pending_deposits: k.pending_deposit_count ?? s.deposits?.pending ?? 0,
        pending_withdrawals: k.pending_withdraw_count ?? s.withdrawals?.pending ?? 0,
        total_deposit_amount: k.lifetime_deposit_amount ?? s.deposits?.total_amount ?? 0,
        total_withdrawal_amount: k.lifetime_withdraw_amount ?? s.withdrawals?.total_amount ?? 0,
        pending_deposit_amount: k.pending_deposit_amount ?? 0,
        pending_withdraw_amount: k.pending_withdraw_amount ?? 0,
        active_games: s.games?.active || 0,
        live_activity: dashRes.data.live_activity || [],
        pending_approvals: dashRes.data.pending_approvals || {},
      });
      setInsights(insightRes.data);
      setRevenue(revRes.data);
      setUserStats(usrRes.data);
      setGameStats(gmeRes.data);
      setWalletSummary(walRes.data);
      setSyncStatus(syncRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const triggerSync = async () => {
    setSyncing(true);
    try { await api.post('/api/admin/sync/trigger'); } catch {}
    finally { setTimeout(() => setSyncing(false), 3000); }
  };

  const exportCSV = async (type) => {
    try {
      const res = await api.get(`/api/admin/export/${type}`, { responseType: 'blob' });
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename=([^;]+)/i);
      const filename = (match?.[1] || `${type}.csv`).replace(/"/g, '');
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  const kpis = insights?.kpis || {};
  const selectedWindow = rangeActive
    ? {
        deposits: { amount: kpis.period_deposit_amount || 0, count: kpis.period_deposit_count || 0 },
        withdrawals: { amount: kpis.period_withdraw_amount || 0, count: kpis.period_withdraw_count || 0 },
        profit: kpis.period_net_revenue || 0,
        new_signups: kpis.period_signups || 0,
      }
    : (insights?.period_cards?.[period === 'day' ? 'today' : period] || {});
  const funnel = insights?.signup_funnel || {};
  const netRev = selectedWindow?.profit ?? (revenue?.net_revenue || 0);
  const fmtMoney = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  const fmtUSD = (v) => `$${(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  const fmtPct = (v) => `${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
  const flowSeries = (insights?.timeseries?.net_flow || []).map((r) => ({
    ts: r.ts,
    In: r.deposits || 0,
    Out: r.withdrawals || 0,
    Profit: r.profit || 0,
  }));
  const signupFunnelSeries = (funnel?.series || []).map((r) => ({
    ts: r.ts,
    Signups: r.signups || 0,
    'First Deposit': r.first_deposit_users || 0,
    'Second Deposit': r.second_deposit_users || 0,
  }));
  const periodCards = insights?.period_cards || {};

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Welcome */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Dashboard</h2>
          <p className="text-sm text-gray-400">Welcome back, {user?.username || user?.email?.split('@')[0]}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative group">
            <button className="p-2 rounded-lg hover:bg-white/5 text-gray-400 transition" data-testid="export-menu-btn">
              <Download className="w-5 h-5" />
            </button>
            <div className="absolute right-0 top-10 w-48 bg-[#1a1f2e] border border-white/10 rounded-xl shadow-xl hidden group-hover:block z-50">
              <button onClick={() => exportCSV('players')} className="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5 rounded-t-xl" data-testid="export-players">Export Players CSV</button>
              <button onClick={() => exportCSV('transactions')} className="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5" data-testid="export-transactions">Export Transactions CSV</button>
              <button onClick={() => exportCSV('game-transactions')} className="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5 rounded-b-xl" data-testid="export-game-txns">Export Game Credits CSV</button>
            </div>
          </div>
          <button onClick={fetchAll} className="p-2 rounded-lg hover:bg-white/5 text-gray-400 transition" data-testid="refresh-dashboard">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Executive filters */}
      <div className="flex flex-wrap items-end justify-between gap-3 rounded-2xl border border-white/10 bg-[#131822] p-4">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[#D4AF37] font-semibold">Executive Metrics</p>
          <p className="text-xs text-gray-500 mt-1">Approved real-cash deposits and withdrawals only. Bonuses and rewards stay in FP credits.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-xl bg-[#0B0E14] border border-white/10 p-1">
            {PERIODS.map((p) => (
              <button
                key={p.id}
                onClick={() => { setPeriod(p.id); setRangeActive(false); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                  !rangeActive && period === p.id ? 'bg-[#D4AF37] text-black' : 'text-gray-400 hover:text-white'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <input
            type="date"
            value={customRange.start ? customRange.start.slice(0, 10) : ''}
            onChange={(e) => setCustomRange((r) => ({ ...r, start: e.target.value ? `${e.target.value}T00:00:00.000Z` : '' }))}
            className="bg-[#0B0E14] border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white"
          />
          <input
            type="date"
            value={customRange.end ? customRange.end.slice(0, 10) : ''}
            onChange={(e) => setCustomRange((r) => ({ ...r, end: e.target.value ? `${e.target.value}T23:59:59.999Z` : '' }))}
            className="bg-[#0B0E14] border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white"
          />
          <button
            onClick={() => {
              if (!customRange.start || !customRange.end) return;
              setRangeActive(true);
              setRangeVersion((v) => v + 1);
            }}
            disabled={!customRange.start || !customRange.end}
            className="px-4 py-1.5 rounded-lg bg-[#D4AF37] text-black text-xs font-semibold disabled:opacity-40"
          >
            Apply
          </button>
        </div>
      </div>

      {/* Top KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={DollarSign} label="Total In" value={fmtMoney(selectedWindow?.deposits?.amount)} sub={`${selectedWindow?.deposits?.count || 0} approved deposits`} color="#22c55e" />
        <KPI icon={ArrowUpCircle} label="Total Out" value={fmtMoney(selectedWindow?.withdrawals?.amount)} sub={`${selectedWindow?.withdrawals?.count || 0} approved withdrawals`} color="#ef4444" />
        <KPI icon={netRev >= 0 ? TrendingUp : TrendingDown} label="Profit" value={fmtMoney(netRev)} sub="IN minus OUT" color={netRev >= 0 ? '#22c55e' : '#ef4444'} />
        <KPI icon={Users} label="New Signups" value={selectedWindow?.new_signups || 0} sub={rangeActive ? 'custom range' : (PERIODS.find(p => p.id === period)?.label || 'period')} color="#3B82F6" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={Users} label="Total Players" value={stats?.total_players || 0} sub={`${stats?.new_24h || 0} new in period`} color="#3B82F6" />
        <KPI icon={ArrowDownCircle} label="Pending Deposits" value={stats?.pending_deposits || 0} sub={fmtMoney(stats?.pending_deposit_amount)} color="#10B981" urgent={stats?.pending_deposits > 0} onClick={() => navigate('/admin/deposit-requests?status=pending')} />
        <KPI icon={ArrowUpCircle} label="Pending Withdrawals" value={stats?.pending_withdrawals || 0} sub={fmtMoney(stats?.pending_withdraw_amount)} color="#EF4444" urgent={stats?.pending_withdrawals > 0} onClick={() => navigate('/admin/withdrawal-requests?status=pending')} />
        <KPI icon={Gamepad2} label="Active Games" value={stats?.active_games || 0} color="#D4AF37" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={CreditCard} label="First Deposit Users" value={`${funnel.first_deposit_users || 0}/${funnel.new_signups || 0}`} sub={`${fmtPct(funnel.first_deposit_conversion_pct)} signup conversion`} color="#22c55e" />
        <KPI icon={CreditCard} label="Second Deposit Users" value={`${funnel.second_deposit_users || 0}/${funnel.new_signups || 0}`} sub={`${fmtPct(funnel.second_after_first_pct)} repeat after first deposit`} color="#D4AF37" />
        <KPI icon={DollarSign} label="Rejected Deposits" value={kpis.period_rejected_deposit_count || 0} sub={fmtMoney(kpis.period_rejected_deposit_amount)} color="#f97316" />
        <KPI icon={ArrowUpCircle} label="Rejected Withdrawals" value={kpis.period_rejected_withdraw_count || 0} sub={fmtMoney(kpis.period_rejected_withdraw_amount)} color="#fb7185" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-[#131822] rounded-2xl border border-white/10 p-5">
          <h3 className="text-sm font-semibold text-gray-300 mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-[#D4AF37]" /> IN / OUT / Profit
          </h3>
          {flowSeries.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-sm text-gray-500 border border-dashed border-white/10 rounded-xl">No approved cash movement in this range</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={flowSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                <XAxis dataKey="ts" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#ffffff10' }} tickLine={false} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#ffffff10' }} tickLine={false} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => fmtMoney(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Area type="monotone" dataKey="In" stroke="#22c55e" fill="#22c55e33" strokeWidth={2} />
                <Area type="monotone" dataKey="Out" stroke="#ef4444" fill="#ef444433" strokeWidth={2} />
                <Area type="monotone" dataKey="Profit" stroke="#D4AF37" fill="#D4AF3733" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="bg-[#131822] rounded-2xl border border-white/10 p-5">
          <h3 className="text-sm font-semibold text-gray-300 mb-4 flex items-center gap-2">
            <Users className="w-4 h-4 text-[#3B82F6]" /> Signup Conversion
          </h3>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <MiniCard label="First Deposit" value={fmtPct(funnel.first_deposit_conversion_pct)} icon={TrendingUp} color="#22c55e" />
            <MiniCard label="Second Deposit" value={fmtPct(funnel.second_deposit_conversion_pct)} icon={TrendingUp} color="#D4AF37" />
            <MiniCard label="First Deposit $" value={fmtMoney(funnel.first_deposit_amount)} icon={DollarSign} color="#22c55e" />
            <MiniCard label="Second Deposit $" value={fmtMoney(funnel.second_deposit_amount)} icon={DollarSign} color="#D4AF37" />
            <MiniCard label="Avg First $" value={fmtMoney(funnel.avg_first_deposit)} icon={DollarSign} color="#38bdf8" />
            <MiniCard label="Avg Second $" value={fmtMoney(funnel.avg_second_deposit)} icon={DollarSign} color="#a78bfa" />
          </div>
          {signupFunnelSeries.length > 0 ? (
            <ResponsiveContainer width="100%" height={170}>
              <BarChart data={signupFunnelSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                <XAxis dataKey="ts" tick={{ fill: '#94a3b8', fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="Signups" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="First Deposit" fill="#22c55e" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Second Deposit" fill="#D4AF37" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[170px] flex items-center justify-center text-xs text-gray-500 border border-dashed border-white/10 rounded-xl">No signup cohort data</div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-5 gap-3">
        {[
          ['today', 'Today'],
          ['yesterday', 'Yesterday'],
          ['week', 'This Week'],
          ['month', 'This Month'],
          ['all_time', 'All Time'],
        ].map(([key, label]) => {
          const row = periodCards[key] || {};
          const profit = row.profit || 0;
          return (
            <div key={key} className="rounded-2xl border border-white/10 bg-[#131822] p-4">
              <p className="text-xs text-gray-500 mb-2">{label}</p>
              <p className={`text-xl font-bold ${profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>{fmtMoney(profit)}</p>
              <p className="text-[10px] text-gray-500 mt-1">IN {fmtMoney(row.deposits?.amount)} / OUT {fmtMoney(row.withdrawals?.amount)}</p>
              <p className="text-[10px] text-gray-500">Signups {row.new_signups || 0}</p>
            </div>
          );
        })}
      </div>

      {/* Revenue Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-[#131822] rounded-2xl border border-white/10 p-5">
          <h3 className="text-sm font-semibold text-gray-400 mb-4 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#D4AF37]" /> Revenue Overview (30 days)</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <MiniStat label="Total Deposits" value={fmtMoney(revenue?.total_deposits)} sub={`${revenue?.deposit_count || 0} txns`} color="#22c55e" />
            <MiniStat label="Total Withdrawals" value={fmtMoney(revenue?.total_withdrawals)} sub={`${revenue?.withdrawal_count || 0} txns`} color="#ef4444" />
            <MiniStat label="Fees Collected" value={fmtMoney(revenue?.total_fees)} color="#D4AF37" />
            <MiniStat label="Net Revenue" value={fmtMoney(netRev)} color={netRev >= 0 ? '#22c55e' : '#ef4444'} trend={netRev >= 0 ? 'up' : 'down'} />
          </div>
          {/* Visual bar for deposits vs withdrawals */}
          <div className="mt-5">
            <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
              <span>Deposits vs Withdrawals</span>
              <span>{fmtMoney(revenue?.total_deposits)} / {fmtMoney(revenue?.total_withdrawals)}</span>
            </div>
            <div className="h-3 rounded-full bg-white/5 overflow-hidden flex">
              {(() => {
                const total = (revenue?.total_deposits || 0) + (revenue?.total_withdrawals || 0);
                const depPct = total > 0 ? ((revenue?.total_deposits || 0) / total * 100) : 50;
                return (
                  <>
                    <div className="h-full rounded-l-full" style={{ width: `${depPct}%`, background: 'linear-gradient(90deg, #22c55e, #16a34a)' }} />
                    <div className="h-full rounded-r-full" style={{ width: `${100 - depPct}%`, background: 'linear-gradient(90deg, #ef4444, #dc2626)' }} />
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Wallet Summary */}
        <div className="bg-[#131822] rounded-2xl border border-white/10 p-5">
          <h3 className="text-sm font-semibold text-gray-400 mb-4 flex items-center gap-2"><Wallet className="w-4 h-4 text-[#D4AF37]" /> Platform Wallets</h3>
          <div className="space-y-3">
            <WalletRow label="Total Cash" value={fmtMoney(walletSummary?.total_cash)} color="#22c55e" />
            <WalletRow label="FP Credits" value={Number(walletSummary?.total_play_credits || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} color="#3B82F6" />
            <WalletRow label="Pending" value={fmtMoney(walletSummary?.total_pending)} color="#eab308" />
            <WalletRow label="Locked Commissions" value={fmtMoney(walletSummary?.total_locked)} color="#f97316" />
            <div className="pt-2 border-t border-white/5">
              <WalletRow label="Combined Total" value={fmtMoney(walletSummary?.combined_total)} color="#D4AF37" bold />
            </div>
          </div>
        </div>
      </div>

      {/* User & Game Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* User Analytics */}
        <div className="bg-[#131822] rounded-2xl border border-white/10 p-5">
          <h3 className="text-sm font-semibold text-gray-400 mb-4 flex items-center gap-2"><Users className="w-4 h-4 text-[#3B82F6]" /> User Analytics (30 days)</h3>
          <div className="grid grid-cols-2 gap-3">
            <MiniCard label="New Users" value={userStats?.new_users || 0} icon={Users} color="#3B82F6" />
            <MiniCard label="Active Sessions" value={userStats?.active_users || 0} icon={Activity} color="#22c55e" />
            <MiniCard label="Growth Rate" value={`${userStats?.growth_rate || 0}%`} icon={TrendingUp} color="#D4AF37" />
            <MiniCard label="Disabled" value={userStats?.disabled_users || 0} icon={Users} color="#ef4444" />
          </div>
        </div>

        {/* Game Analytics */}
        <div className="bg-[#131822] rounded-2xl border border-white/10 p-5">
          <h3 className="text-sm font-semibold text-gray-400 mb-4 flex items-center gap-2"><Gamepad2 className="w-4 h-4 text-[#EC4899]" /> Game Activity (30 days)</h3>
          <div className="grid grid-cols-2 gap-3">
            <MiniCard label="Total Loads" value={gameStats?.total_loads || 0} icon={ArrowDownCircle} color="#6C5CE7" />
            <MiniCard label="Load Volume" value={fmtMoney(gameStats?.load_volume)} icon={DollarSign} color="#6C5CE7" />
            <MiniCard label="Total Redeems" value={gameStats?.total_redeems || 0} icon={ArrowUpCircle} color="#22c55e" />
            <MiniCard label="Redeem Volume" value={fmtMoney(gameStats?.redeem_volume)} icon={DollarSign} color="#22c55e" />
          </div>
          {/* By Provider */}
          {gameStats?.by_provider?.length > 0 && (
            <div className="mt-4 pt-3 border-t border-white/5">
              <p className="text-[10px] text-gray-500 mb-2 font-medium">BY PROVIDER</p>
              <div className="space-y-1.5">
                {gameStats.by_provider.slice(0, 5).map(p => (
                  <div key={p.provider} className="flex items-center justify-between text-xs">
                    <span className="text-gray-400 capitalize">{p.provider}</span>
                    <div className="flex gap-3">
                      <span className="text-gray-300">{p.count} txns</span>
                      <span className="text-[#D4AF37] font-medium">{fmtMoney(p.volume)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Live Activity */}
      <div className="bg-[#131822] rounded-2xl border border-white/10 p-5">
        <h3 className="text-sm font-semibold text-gray-400 mb-4 flex items-center gap-2"><Clock className="w-4 h-4 text-[#D4AF37]" /> Live Activity (24h)</h3>
        {(stats?.live_activity || []).length === 0 ? (
          <p className="text-center text-gray-500 py-6 text-sm">No recent activity</p>
        ) : (
          <div className="space-y-2">
            {(stats?.live_activity || []).slice(0, 10).map((act, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-[#0B0E14]">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ background: act.type === 'deposit' ? 'rgba(34,197,94,0.15)' : act.type === 'payout' ? 'rgba(239,68,68,0.15)' : 'rgba(212,175,55,0.15)' }}>
                    {act.type === 'deposit' ? <ArrowDownCircle className="w-4 h-4 text-green-400" /> :
                     act.type === 'payout' ? <ArrowUpCircle className="w-4 h-4 text-red-400" /> :
                     <Activity className="w-4 h-4 text-[#D4AF37]" />}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">{act.username || 'Unknown'}</p>
                    <p className="text-xs text-gray-500 capitalize">{act.type} {act.action && `- ${act.action}`}</p>
                  </div>
                </div>
                <div className="text-right">
                  {act.amount && <p className="text-sm font-semibold" style={{ color: act.type === 'deposit' ? '#22c55e' : '#ef4444' }}>${Number(act.amount).toFixed(2)}</p>}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                    act.status === 'APPROVED' || act.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                    act.status === 'UNDER_REVIEW' || act.status === 'PENDING_REVIEW' ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-gray-500/20 text-gray-400'
                  }`}>{act.status}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sync Status */}
      {syncStatus?.last_sync && (
        <div className="bg-[#131822] rounded-2xl border border-white/10 p-5" data-testid="sync-status-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-400 flex items-center gap-2"><RefreshCw className="w-4 h-4 text-[#D4AF37]" /> Balance Sync</h3>
            <button onClick={triggerSync} disabled={syncing} className="text-xs px-3 py-1.5 rounded-lg bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37]/20 transition disabled:opacity-50" data-testid="trigger-sync-btn">
              {syncing ? 'Syncing...' : 'Sync Now'}
            </button>
          </div>
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-[#0B0E14] rounded-lg p-2.5 text-center">
              <p className="text-sm font-bold text-green-400">{syncStatus.last_sync?.synced || 0}</p>
              <p className="text-[9px] text-gray-500">Synced</p>
            </div>
            <div className="bg-[#0B0E14] rounded-lg p-2.5 text-center">
              <p className="text-sm font-bold text-red-400">{syncStatus.last_sync?.errors || 0}</p>
              <p className="text-[9px] text-gray-500">Errors</p>
            </div>
            <div className="bg-[#0B0E14] rounded-lg p-2.5 text-center">
              <p className="text-sm font-bold text-blue-400">{syncStatus.last_sync?.total || 0}</p>
              <p className="text-[9px] text-gray-500">Total</p>
            </div>
            <div className="bg-[#0B0E14] rounded-lg p-2.5 text-center">
              <p className="text-sm font-bold text-[#D4AF37]">{syncStatus.last_sync?.value ? new Date(syncStatus.last_sync.value).toLocaleTimeString() : 'Never'}</p>
              <p className="text-[9px] text-gray-500">Last Sync</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KPI({ icon: Icon, label, value, sub, color, urgent, onClick }) {
  return (
    <div
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => { if (onClick && (e.key === 'Enter' || e.key === ' ')) onClick(); }}
      className={`bg-[#131822] rounded-2xl border p-4 ${urgent ? 'border-yellow-500/30' : 'border-white/10'} ${onClick ? 'cursor-pointer hover:border-[#D4AF37]/50 hover:bg-white/[0.03] transition' : ''}`}
    >
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${color}15` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div>
          <p className="text-[11px] text-gray-500">{label}</p>
          <p className="text-xl font-bold text-white">{value}</p>
          {sub && <p className="text-[10px] text-gray-500">{sub}</p>}
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, sub, color, trend }) {
  return (
    <div>
      <p className="text-[10px] text-gray-500 mb-1">{label}</p>
      <div className="flex items-center gap-1">
        <p className="text-lg font-bold" style={{ color }}>{value}</p>
        {trend === 'up' && <TrendingUp className="w-3.5 h-3.5 text-green-400" />}
        {trend === 'down' && <TrendingDown className="w-3.5 h-3.5 text-red-400" />}
      </div>
      {sub && <p className="text-[10px] text-gray-500">{sub}</p>}
    </div>
  );
}

function WalletRow({ label, value, color, bold }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`text-xs ${bold ? 'text-white font-semibold' : 'text-gray-400'}`}>{label}</span>
      <span className={`text-sm font-mono ${bold ? 'font-bold' : 'font-medium'}`} style={{ color }}>{value}</span>
    </div>
  );
}

function MiniCard({ label, value, icon: Icon, color }) {
  return (
    <div className="rounded-xl p-3" style={{ background: `${color}08`, border: `1px solid ${color}15` }}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className="w-3.5 h-3.5" style={{ color }} />
        <p className="text-[10px] text-gray-500">{label}</p>
      </div>
      <p className="text-base font-bold" style={{ color }}>{value}</p>
    </div>
  );
}
